import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { LS } from "../data/localStoreKeys";

export type LoginMethod = "vipps" | "aws" | "NOT_LOGGED_IN";
export type User = {
  AccessToken: string;
  RefreshToken: string;
  IdToken: string;
  attributes: Record<any, any>;
};

interface AuthState {
  email: string;
  isLoggedIn: boolean;
  loginMethod: LoginMethod;
  user: User;
  userAttributes: any;
  creditCheckStatus:
    | "APPROVED"
    | "PENDING"
    | "DECLINED"
    | "IDENTITY_NOT_VERIFIED"
    | "DETAILS_NOT_MATCHED"
    | "CREDIT_CHECK_IMPOSSIBLE"
    // Frontend-only default used in checkout before any credit check is fetched.
    // This value does not exist in the backend.
    | "NOT_FETCHED";
  signUpFormIsOpen: boolean;
  beforeNextPageErrors: boolean;
  // Add authInitialized flag
  authInitialized: boolean;
}

const initialState: AuthState = {
  email: "",
  isLoggedIn: false,
  user: {
    AccessToken: "",
    IdToken: "",
    RefreshToken: "",
    attributes: {},
  },
  userAttributes: {}, // contains user name, email, address, phone, verification status, confirmation status & more
  loginMethod: "NOT_LOGGED_IN",
  creditCheckStatus: "NOT_FETCHED",
  signUpFormIsOpen: true,
  beforeNextPageErrors: false,
  // Initialize authInitialized to false
  authInitialized: false,
};

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setEmail: (state, action: PayloadAction<string>) => {
      state.email = action.payload.toLowerCase();
    },
    setIsLoggedIn: (state, action: PayloadAction<boolean>) => {
      state.isLoggedIn = action.payload;
    },
    setUser: (state, action: PayloadAction<any>) => {
      state.user = action.payload;
      localStorage.setItem(LS.USER, JSON.stringify(action.payload));
    },
    setUserAttributes: (state, action: PayloadAction<any>) => {
      state.userAttributes = action.payload;
    },
    setLoginMethod: (state, action: PayloadAction<LoginMethod>) => {
      state.loginMethod = action.payload;
      localStorage.setItem(LS.AUTH_PROVIDER, action.payload);
    },
    setCreditCheckStatus: (state, action: PayloadAction<AuthState["creditCheckStatus"]>) => {
      state.creditCheckStatus = action.payload;
    },
    setSignUpFormIsOpen: (state, action: PayloadAction<boolean>) => {
      state.signUpFormIsOpen = action.payload;
    },
    setBeforeNextPageErrors: (state, action: PayloadAction<boolean>) => {
      state.beforeNextPageErrors = action.payload;
    },
    // Add setAuthInitialized reducer
    setAuthInitialized: (state, action: PayloadAction<boolean>) => {
      state.authInitialized = action.payload;
    },
  },
});

export const {
  setEmail,
  setIsLoggedIn,
  setUser,
  setUserAttributes,
  setLoginMethod,
  setCreditCheckStatus,
  setSignUpFormIsOpen,
  setBeforeNextPageErrors,
  setAuthInitialized, // export the new action
} = authSlice.actions;

export const selectEmail = (state: any) => state.auth.email;
export const selectIsLoggedIn = (state: any) => state.auth.isLoggedIn;
export const selectUser = (state: any) => state.auth.user;
export const selectUserAttributes = (state: any) => state.auth.userAttributes;
export const selectLoginMethod = (state: any) => state.auth.loginMethod;
export const selectCreditCheckStatus = (state: any) => state.auth.creditCheckStatus;
export const selectSignUpFormIsOpen = (state: any) => state.auth.signUpFormIsOpen;
export const selectBeforeNextPageErrors = (state: any) => state.auth.beforeNextPageErrors;
// Add selector for authInitialized
export const selectAuthInitialized = (state: any) => state.auth.authInitialized;

export default authSlice.reducer;
